/**
 * Helpers for safely rendering agent HTML artifacts inside an opaque
 * (no ``allow-same-origin``) ``srcDoc`` iframe — the media preview dialog and
 * the public share page (``app/share/[token]``).
 *
 * The srcdoc iframe inherits its document base URL from the *parent* page, so
 * relative URLs and ``#anchor`` links resolve against the Soku origin and a
 * click navigates the iframe away to the parent page. The injected snippet
 * keeps all navigation inside the iframe; external links either relay to the
 * parent (chat preview — its sandbox has no ``allow-popups``) or open a new
 * tab directly (share page — its sandbox can escape via popups).
 */

/** ``postMessage`` type used to relay an external link from the iframe to the parent. */
export const ARTIFACT_LINK_MESSAGE = 'artifact-link'
/** Iframe -> parent: one annotated element, new or edited, keyed by ``qid``. */
export const ARTIFACT_ELEMENT_QUOTE_MESSAGE = 'artifact-element-quote'
/** Iframe -> parent: the note behind ``qid`` was deleted from its own bubble. */
export const ARTIFACT_ELEMENT_QUOTE_REMOVE_MESSAGE =
  'artifact-element-quote-remove'
/** Parent -> iframe: annotating is on or off. */
export const ARTIFACT_ELEMENT_QUOTE_MODE_MESSAGE = 'artifact-element-quote-mode'
/**
 * Parent -> iframe: the ``qid``s the parent still holds, in list order.
 *
 * The parent list is what the user sends, so it decides what exists: a pin
 * whose id is missing from this message disappears, and the rest renumber to
 * match the cards. That is how removing a card in the dialog takes its pin
 * with it, and how the parent's selection cap is enforced without the injected
 * snippet knowing what the cap is.
 */
export const ARTIFACT_ELEMENT_QUOTE_STATE_MESSAGE =
  'artifact-element-quote-state'
/** ``postMessage`` type the print iframe posts to the parent once the browser
 *  print dialog closes, so the parent can remove the temporary frame. */
export const ARTIFACT_PRINT_DONE_MESSAGE = 'artifact-print-done'

const REPORT_CJK_STYLESHEET_ID = 'nex-report-cjk-fonts-v2'
const REPORT_CJK_STYLESHEET_PATH =
  '/fonts/report-cjk-v2/report-fonts.css'
const LEGACY_REPORT_FONT_STYLE_RE =
  /<style\b[^>]*\bid\s*=\s*(["'])nex-report-fonts\1[^>]*>[\s\S]*?<\/style\s*>/gi
const LITERAL_REPORT_FONT_STACKS: Array<[RegExp, string]> = [
  [
    /((?:font-family|--[\w-]+)\s*:\s*)(["']?)PP Editorial New\2(\s*,)(?!\s*["']?Nex Report CJK Serif v1)/gi,
    '$1"PP Editorial New", "Nex Report CJK Serif v1"$3',
  ],
  [
    /((?:font-family|--[\w-]+)\s*:\s*)(["']?)PP Neue Montreal\2(\s*,)(?!\s*["']?Nex Report CJK Sans v1)/gi,
    '$1"PP Neue Montreal", "Nex Report CJK Sans v1"$3',
  ],
]

function removeLegacyReportFontStyle(html: string): string {
  return html.replace(LEGACY_REPORT_FONT_STYLE_RE, '').replace(
    /<link\b[^>]*\bhref\s*=\s*(["'])[^"']*\/fonts\/report-cjk-v1\/report-fonts\.css\1[^>]*>/gi,
    (link) => link
      .replace(/\/fonts\/report-cjk-v1\/report-fonts\.css/i, REPORT_CJK_STYLESHEET_PATH)
      .replace(/\s+id\s*=\s*(["'])[^"']*\1/i, '')
      .replace(/<link/i, `<link id="${REPORT_CJK_STYLESHEET_ID}"`),
  )
}

function upgradeLiteralReportFontStacks(html: string): string {
  return LITERAL_REPORT_FONT_STACKS.reduce(
    (upgraded, [pattern, replacement]) => upgraded.replace(pattern, replacement),
    html,
  )
}

function reportCjkStylesheetLink(html: string, fontBase?: string): string {
  if (!fontBase || html.includes(REPORT_CJK_STYLESHEET_ID)) return ''
  let href: string
  try {
    href = new URL(REPORT_CJK_STYLESHEET_PATH, fontBase).href
  } catch {
    return ''
  }
  const escapedHref = href.replaceAll('&', '&amp;').replaceAll('"', '&quot;')
  return `<link id="${REPORT_CJK_STYLESHEET_ID}" rel="stylesheet" href="${escapedHref}">`
}

// Injected at the top of the artifact ``<head>``. Two jobs, both keeping
// navigation inside the opaque iframe:
//   1. ``<base href="about:srcdoc">`` roots relative URLs at the iframe's own
//      document instead of the parent page URL, so a stray relative resource
//      cannot reach the Soku origin. Best-effort only: the parent page's CSP
//      carries ``base-uri 'self'``, which srcdoc documents inherit, so in
//      production the browser refuses the ``about:srcdoc`` base (verified on a
//      live share page — ``document.baseURI`` stays the parent URL). The click
//      handler below therefore never relies on the base and handles fragments
//      and non-http(s) links itself.
//   2. A capture-phase click handler on every ``<a href>``:
//        - ``#anchor``   -> scroll within the iframe (never navigates away)
//        - ``http(s)``   -> the caller-supplied external-link action
//          (protocol-relative ``//host`` links are normalized to ``https:``
//          first — they are valid external navigations and would otherwise
//          fall into the blocked branch and become dead links)
//        - anything else -> blocked, so relative / ``javascript:`` / ``mailto:``
//                           links cannot escape to the parent origin.
//      Capture phase + top-of-head injection let it run before any
//      agent-shipped handler can swallow the event. On the external and
//      blocked branches the handler also calls ``stopImmediatePropagation``,
//      so an author-shipped click listener cannot re-trigger the navigation
//      it just suppressed (e.g. a card-wide handler doing
//      ``location.href = a.href``, which would navigate the sandboxed iframe
//      and crash the destination). The fragment branch deliberately lets the
//      event propagate: interactive artifacts routinely hang tab/section
//      logic off ``#anchor`` clicks, and the scroll here is additive.
//      The handler intentionally ignores author-declared ``target``
//      attributes: no in-frame target can produce a working page here (any
//      http(s) document navigated inside the opaque sandbox crashes on
//      cookie/storage access), so every external click routes through the
//      same action. ``closest("a[href]")`` also matches SVG ``<a href>``,
//      and the string-based href checks keep SVG anchors on the same path as
//      HTML ones.
function buildLinkInterceptorSnippet(externalLinkAction: string): string {
  return `<base href="about:srcdoc"><script>(function(){document.addEventListener("click",function(e){var el=e.target;var a=el&&el.closest?el.closest("a[href]"):null;if(!a)return;var href=a.getAttribute("href");if(!href)return;if(href.charAt(0)==="#"){e.preventDefault();var id=href.slice(1);if(!id){window.scrollTo(0,0);return;}var t=null;try{t=document.getElementById(id);}catch(_){}if(!t){try{t=document.querySelector('a[name="'+id+'"]');}catch(_){}}if(t&&t.scrollIntoView){t.scrollIntoView({behavior:"smooth"});}return;}if(href.indexOf("//")===0){href="https:"+href;}var lower=href.toLowerCase();if(lower.indexOf("http://")===0||lower.indexOf("https://")===0){e.preventDefault();e.stopImmediatePropagation();${externalLinkAction}return;}e.preventDefault();e.stopImmediatePropagation();},true);})();</script>`
}

// Chat preview / print iframes render with ``sandbox="allow-scripts"`` — no
// ``allow-popups`` — so the iframe cannot open tabs itself and relays external
// links to the parent instead.
const LINK_INTERCEPTOR_SNIPPET = buildLinkInterceptorSnippet(
  `try{parent.postMessage({type:"${ARTIFACT_LINK_MESSAGE}",href:href},"*");}catch(_){}`,
)

// The public share page iframe additionally carries ``allow-popups`` +
// ``allow-popups-to-escape-sandbox``, so the iframe opens external links in a
// fresh, non-sandboxed tab directly — there is no trusted parent script to
// relay through on a public page. ``noopener,noreferrer`` matches the chat
// relay path: no reference back into the sandboxed document, and no referrer
// (the share URL carries the public token) leaking to the destination.
const SHARE_LINK_INTERCEPTOR_SNIPPET = buildLinkInterceptorSnippet(
  `try{window.open(href,"_blank","noopener,noreferrer");}catch(_){}`,
)

/** Copy the in-frame note bubble renders. Passed in because the snippet runs
 *  inside the artifact document, where next-intl cannot reach. */
export interface ArtifactElementQuoteLabels {
  /** Placeholder inside the note field. */
  placeholder: string
  /** Confirms a brand-new note. */
  add: string
  /** Confirms an edit to an existing note. */
  save: string
  cancel: string
  /** Drops the pin and its note. */
  remove: string
  /** One-line keyboard hint under the field. */
  hint: string
}

/**
 * Embed a caller-supplied string as a JS literal inside the injected snippet.
 * ``JSON.stringify`` handles quoting and control characters; ``<`` additionally
 * becomes ``<`` so no translation can close the ``<script>`` element it
 * sits in.
 */
function jsLiteral(value: string): string {
  return JSON.stringify(value).replaceAll('<', '\\u003c')
}

/**
 * The element-annotation tooling injected into the preview iframe.
 *
 * The point of living inside the frame rather than overlaying from the parent
 * is position: the artifact scrolls in its own document, so a pin drawn out
 * here rides the element it belongs to for free, while a parent-side overlay
 * would need the frame to stream every scroll offset back across the origin
 * boundary. It also puts the note field next to the thing being annotated —
 * which is the whole point of annotating rather than listing.
 *
 * Flow: hover highlights the element under the cursor, a click opens a note
 * bubble anchored to it, and confirming posts the element snapshot with its
 * note to the parent and leaves a numbered pin behind. Clicking a pin reopens
 * its note for editing or removal.
 *
 * The parent list is the source of truth for what is selected: it answers with
 * the ids it kept (see ``ARTIFACT_ELEMENT_QUOTE_STATE_MESSAGE``), and pins not
 * in that answer disappear. That is what makes removing a card in the dialog
 * take its pin with it, and what enforces the parent's selection cap without
 * this snippet knowing the number.
 *
 * Every rule carries ``!important`` and every colour is literal: the snippet
 * shares a document with agent-written CSS that routinely styles bare tags,
 * and inherited styling would otherwise make the bubble unreadable. The UI
 * marks itself with ``data-nex-quote-ui`` so the picker never offers its own
 * chrome as an annotation target.
 */
function buildElementQuoteSnippet(labels: ArtifactElementQuoteLabels): string {
  return `<style id="nex-artifact-element-quote-style">.nex-artifact-element-quote-cursor *{cursor:crosshair!important}</style><script>
(function(){
var CURSOR="nex-artifact-element-quote-cursor";
var UI="data-nex-quote-ui";
var FONT="13px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif";
var L={placeholder:${jsLiteral(labels.placeholder)},add:${jsLiteral(labels.add)},save:${jsLiteral(labels.save)},cancel:${jsLiteral(labels.cancel)},remove:${jsLiteral(labels.remove)},hint:${jsLiteral(labels.hint)}};
var active=false;var current=null;var overlay=null;var pins=[];var seq=0;var composer=null;

function cap(v,n){v=String(v||"").replace(/\\s+/g," ").trim();return v.length>n?v.slice(0,n):v;}
function hash(v){v=String(v||"");var h=0;for(var i=0;i<v.length;i++){h=((h<<5)-h+v.charCodeAt(i))|0;}return (h>>>0).toString(16);}
function cssIdent(v){v=String(v||"");if(!v)return null;if(window.CSS&&CSS.escape)return CSS.escape(v);return /^[A-Za-z_][A-Za-z0-9_-]*$/.test(v)?v:null;}
function attr(name,value){value=String(value||"");return value?'['+name+'="'+value.replace(/"/g,'\\\\\\"')+'"]':null;}
function nth(el){var i=1,s=el;while((s=s.previousElementSibling)){if(s.tagName===el.tagName)i++;}return el.tagName.toLowerCase()+":nth-of-type("+i+")";}
function path(el){var parts=[];var n=el;while(n&&n.nodeType===1&&parts.length<8){var id=cssIdent(n.id);parts.unshift(id?"#"+id:nth(n));if(id)break;n=n.parentElement;}return parts.join(" > ");}
function hints(el){var out=[];var id=cssIdent(el.id);if(id)out.push("#"+id);var testid=el.getAttribute("data-testid");var nexid=el.getAttribute("data-nex-element-id");var role=el.getAttribute("role");[attr("data-nex-element-id",nexid),attr("data-testid",testid),role?el.tagName.toLowerCase()+'[role="'+role.replace(/"/g,'\\\\\\"')+'"]':null,path(el)].forEach(function(v){if(v&&out.indexOf(v)===-1)out.push(v);});return out.slice(0,6);}
function label(el,text){return cap(el.getAttribute("aria-label")||el.getAttribute("title")||el.getAttribute("alt")||text,160)||null;}
function rect(el){var r=el.getBoundingClientRect();function round(v){return Math.round(v*100)/100;}return{x:round(r.left),y:round(r.top),width:round(r.width),height:round(r.height)};}
function snapshot(el){var text=cap(el.innerText||el.textContent||"",1000);var html=cap(el.outerHTML||"",2000);return{tag:el.tagName.toLowerCase(),role:el.getAttribute("role")||null,label:label(el,text),text:text||null,html_excerpt:html||null,selector_hints:hints(el),dom_path:path(el),text_hash:text?hash(text):null,outer_html_hash:html?hash(html):null,rect:rect(el)};}

function isUi(node){var el=node&&node.nodeType===1?node:node&&node.parentElement;return !!(el&&el.closest&&el.closest("["+UI+"]"));}
function pick(target){var el=target&&target.nodeType===1?target:target&&target.parentElement;if(!el||!el.closest)return null;if(isUi(el))return null;el=el.closest("button,a,input,textarea,select,[role],section,article,main,nav,header,footer,aside,tr,table,figure,figcaption,img,blockquote,canvas,svg,h1,h2,h3,h4,h5,h6,li,p,div");if(!el||el===document.documentElement||el===document.body)return null;if(isUi(el))return null;var tag=el.tagName.toLowerCase();if(tag==="script"||tag==="style"||tag==="link"||tag==="meta")return null;return el;}
function style(node,css){node.style.cssText=css;}

function ensureOverlay(){if(overlay)return overlay;overlay=document.createElement("div");overlay.setAttribute("aria-hidden","true");overlay.setAttribute(UI,"overlay");style(overlay,"position:fixed!important;z-index:2147483645!important;pointer-events:none!important;border:2px solid #2563eb!important;background:rgba(37,99,235,.08)!important;border-radius:4px!important;box-sizing:border-box!important;display:none!important;margin:0!important;padding:0!important");document.body.appendChild(overlay);return overlay;}
function show(el){current=el;var r=el.getBoundingClientRect();var o=ensureOverlay();o.style.setProperty("display","block","important");o.style.setProperty("left",r.left+"px","important");o.style.setProperty("top",r.top+"px","important");o.style.setProperty("width",r.width+"px","important");o.style.setProperty("height",r.height+"px","important");}
function hideOverlay(){current=null;if(overlay)overlay.style.setProperty("display","none","important");}
function setActive(next){active=!!next;if(active){document.documentElement.classList.add(CURSOR);}else{document.documentElement.classList.remove(CURSOR);hideOverlay();}}

function placePin(pin,index){var r=pin.el.getBoundingClientRect();var n=pin.node;n.textContent=String(index+1);var vw=document.documentElement.clientWidth||window.innerWidth;var vh=document.documentElement.clientHeight||window.innerHeight;var gone=(r.width===0&&r.height===0)||r.bottom<0||r.top>vh||r.right<0||r.left>vw;n.style.setProperty("display",gone?"none":"flex","important");if(gone)return;n.style.setProperty("left",Math.max(2,r.left-11)+"px","important");n.style.setProperty("top",Math.max(2,r.top-11)+"px","important");}
function repaintPins(){for(var i=0;i<pins.length;i++)placePin(pins[i],i);}
function createPin(qid,el,note){var n=document.createElement("div");n.setAttribute(UI,"pin");n.setAttribute("role","button");n.setAttribute("tabindex","-1");style(n,"position:fixed!important;z-index:2147483646!important;display:flex!important;align-items:center!important;justify-content:center!important;width:22px!important;height:22px!important;box-sizing:border-box!important;border-radius:999px!important;border:2px solid #ffffff!important;background:#2563eb!important;color:#ffffff!important;font:600 11px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif!important;letter-spacing:0!important;cursor:pointer!important;box-shadow:0 2px 8px rgba(15,23,42,.35)!important;margin:0!important;padding:0!important;text-indent:0!important;opacity:1!important;visibility:visible!important;transform:none!important");n.addEventListener("click",function(e){e.preventDefault();e.stopPropagation();openComposer(el,qid);},true);document.body.appendChild(n);var pin={qid:qid,el:el,node:n,note:note||""};pins.push(pin);return pin;}
function findPin(qid){for(var i=0;i<pins.length;i++){if(pins[i].qid===qid)return pins[i];}return null;}
function findPinByEl(el){for(var i=0;i<pins.length;i++){if(pins[i].el===el)return pins[i];}return null;}
function dropPin(qid){for(var i=0;i<pins.length;i++){if(pins[i].qid===qid){pins[i].node.remove();pins.splice(i,1);return;}}}
function syncPins(qids){var order={};var i;for(i=0;i<qids.length;i++){if(typeof qids[i]==="string")order[qids[i]]=i;}for(i=pins.length-1;i>=0;i--){if(!(pins[i].qid in order)){pins[i].node.remove();pins.splice(i,1);}}pins.sort(function(a,b){return order[a.qid]-order[b.qid];});repaintPins();if(composer&&composer.qid&&!findPin(composer.qid))closeComposer();}

function button(text,variant){var b=document.createElement("button");b.type="button";b.textContent=text;var base="display:inline-flex!important;align-items:center!important;justify-content:center!important;height:26px!important;padding:0 10px!important;box-sizing:border-box!important;border-radius:6px!important;font:500 12px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif!important;cursor:pointer!important;margin:0!important;text-transform:none!important;letter-spacing:0!important;box-shadow:none!important;opacity:1!important;visibility:visible!important;min-width:0!important;width:auto!important";if(variant==="primary"){style(b,base+";border:1px solid #2563eb!important;background:#2563eb!important;color:#ffffff!important");}else if(variant==="danger"){style(b,base+";border:1px solid transparent!important;background:transparent!important;color:#b91c1c!important;padding:0 6px!important");}else{style(b,base+";border:1px solid #cbd5e1!important;background:#ffffff!important;color:#334155!important");}return b;}

function placeComposer(){if(!composer)return;var r=composer.el.getBoundingClientRect();var root=composer.root;var w=root.offsetWidth||288;var h=root.offsetHeight||150;var vw=document.documentElement.clientWidth||window.innerWidth;var vh=document.documentElement.clientHeight||window.innerHeight;var left=r.left;if(left+w>vw-8)left=vw-w-8;if(left<8)left=8;var top=r.bottom+8;if(top+h>vh-8)top=r.top-h-8;if(top<8)top=Math.min(8,vh-h-8);if(top<8)top=8;root.style.setProperty("left",left+"px","important");root.style.setProperty("top",top+"px","important");}
function closeComposer(){if(!composer)return;composer.root.remove();composer=null;}
function openComposer(el,qid){closeComposer();hideOverlay();var pin=qid?findPin(qid):null;
var root=document.createElement("div");root.setAttribute(UI,"composer");style(root,"position:fixed!important;z-index:2147483647!important;width:288px!important;box-sizing:border-box!important;padding:10px!important;margin:0!important;border:1px solid #cbd5e1!important;border-radius:10px!important;background:#ffffff!important;color:#0f172a!important;box-shadow:0 12px 32px rgba(15,23,42,.22)!important;font:"+FONT+"!important;text-align:left!important;opacity:1!important;visibility:visible!important;transform:none!important;direction:ltr!important");
var head=document.createElement("div");style(head,"display:flex!important;align-items:center!important;gap:6px!important;margin:0 0 6px!important;padding:0!important;font:500 11px/1.4 "+FONT.split(" ").slice(1).join(" ")+"!important;color:#64748b!important");
var tag=document.createElement("span");tag.textContent="<"+el.tagName.toLowerCase()+">";style(tag,"display:inline-block!important;padding:1px 5px!important;border:1px solid #e2e8f0!important;border-radius:4px!important;background:#f8fafc!important;color:#334155!important;font:500 11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace!important");
var title=document.createElement("span");title.textContent=cap(label(el,el.innerText||el.textContent||"")||"",48);style(title,"flex:1 1 auto!important;min-width:0!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;color:#64748b!important;font:400 11px/1.4 "+FONT.split(" ").slice(1).join(" ")+"!important");
head.appendChild(tag);head.appendChild(title);
var field=document.createElement("textarea");field.placeholder=L.placeholder;field.value=pin?pin.note:"";field.setAttribute("rows","3");style(field,"display:block!important;width:100%!important;min-height:66px!important;max-height:160px!important;box-sizing:border-box!important;resize:vertical!important;margin:0 0 6px!important;padding:6px 8px!important;border:1px solid #cbd5e1!important;border-radius:6px!important;background:#ffffff!important;color:#0f172a!important;font:"+FONT+"!important;outline:none!important;box-shadow:none!important;text-align:left!important;opacity:1!important;visibility:visible!important");
var hint=document.createElement("div");hint.textContent=L.hint;style(hint,"margin:0 0 8px!important;padding:0!important;color:#94a3b8!important;font:400 11px/1.4 "+FONT.split(" ").slice(1).join(" ")+"!important");
var row=document.createElement("div");style(row,"display:flex!important;align-items:center!important;gap:6px!important;margin:0!important;padding:0!important");
if(pin){var del=button(L.remove,"danger");del.addEventListener("click",function(e){e.preventDefault();e.stopPropagation();removeComposerTarget();},true);row.appendChild(del);}
var spacer=document.createElement("div");style(spacer,"flex:1 1 auto!important;margin:0!important;padding:0!important");row.appendChild(spacer);
var cancel=button(L.cancel,"ghost");cancel.addEventListener("click",function(e){e.preventDefault();e.stopPropagation();closeComposer();},true);row.appendChild(cancel);
var submit=button(pin?L.save:L.add,"primary");submit.addEventListener("click",function(e){e.preventDefault();e.stopPropagation();submitComposer();},true);row.appendChild(submit);
root.appendChild(head);root.appendChild(field);root.appendChild(hint);root.appendChild(row);
document.body.appendChild(root);
composer={root:root,field:field,el:el,qid:pin?pin.qid:null};
placeComposer();
field.addEventListener("keydown",function(e){if(e.key==="Escape"){e.preventDefault();e.stopPropagation();closeComposer();return;}if(e.key==="Enter"&&(e.metaKey||e.ctrlKey)){e.preventDefault();e.stopPropagation();submitComposer();}},true);
try{field.focus({preventScroll:true});}catch(_){field.focus();}}

function submitComposer(){if(!composer)return;var el=composer.el;var note=cap(composer.field.value,500);var pin=composer.qid?findPin(composer.qid):findPinByEl(el);var qid;if(pin){qid=pin.qid;pin.note=note;}else{qid="q"+(++seq);pin=createPin(qid,el,note);}var snap=snapshot(el);if(note)snap.note=note;try{parent.postMessage({type:"${ARTIFACT_ELEMENT_QUOTE_MESSAGE}",qid:qid,element:snap},"*");}catch(_){}closeComposer();repaintPins();}
function removeComposerTarget(){if(!composer||!composer.qid)return;var qid=composer.qid;dropPin(qid);try{parent.postMessage({type:"${ARTIFACT_ELEMENT_QUOTE_REMOVE_MESSAGE}",qid:qid},"*");}catch(_){}closeComposer();repaintPins();}

function reposition(){repaintPins();placeComposer();if(active&&current&&!composer)show(current);}

window.addEventListener("message",function(e){var d=e.data;if(!d)return;if(d.type==="${ARTIFACT_ELEMENT_QUOTE_MODE_MESSAGE}"){setActive(d.active);return;}if(d.type==="${ARTIFACT_ELEMENT_QUOTE_STATE_MESSAGE}"){syncPins(Array.isArray(d.qids)?d.qids:[]);return;}});
document.addEventListener("mouseover",function(e){if(!active||composer||isUi(e.target))return;var el=pick(e.target);if(el)show(el);},true);
document.addEventListener("mousemove",function(e){if(!active||composer||isUi(e.target))return;var el=pick(e.target);if(el&&el!==current)show(el);},true);
document.addEventListener("scroll",reposition,true);
window.addEventListener("resize",reposition);
document.addEventListener("click",function(e){
  // The pin and the bubble run their own handlers; letting the event through
  // is what reaches them, since this capture-phase listener fires first.
  if(isUi(e.target))return;
  var el=pick(e.target);
  if(composer){
    // A half-written note outranks a new target: retargeting would throw the
    // typing away, and there is a visible Cancel for saying so on purpose.
    if(composer.field.value.trim())return;
    if(!el){closeComposer();return;}
    e.preventDefault();e.stopPropagation();openComposer(el,null);return;
  }
  if(!active||!el)return;
  e.preventDefault();e.stopPropagation();
  openComposer(el,null);
},true);
})();
</script>`
}

// Injected into the temporary print iframe (``Download as PDF``). The print
// MUST be triggered from inside the frame: it keeps the same opaque-origin
// sandbox as the preview (``allow-scripts allow-modals``, no
// ``allow-same-origin``), so the parent cannot reach ``frame.contentWindow.print()``
// across origins. ``allow-modals`` lets the sandboxed document open the browser
// print dialog itself. Fires once the document (and its images) finish loading,
// then relays ``afterprint`` to the parent so the frame can be removed. If
// ``print()`` throws (e.g. modals still blocked) it relays immediately so the
// parent does not leak the frame until the 60s fallback.
const PRINT_TRIGGER_SNIPPET = `<script>(function(){function done(){try{parent.postMessage({type:"${ARTIFACT_PRINT_DONE_MESSAGE}"},"*");}catch(_){}}function go(){try{window.focus();}catch(_){}window.addEventListener("afterprint",done,{once:true});try{window.print();}catch(_){done();}}if(document.readyState==="complete"){setTimeout(go,50);}else{window.addEventListener("load",function(){setTimeout(go,50);});}})();</script>`

// Injected into the thumbnail tile's iframe, which renders with ``sandbox=""``
// (no ``allow-scripts``) so agent-shipped JS never runs in the always-on chat
// feed. Agent report HTML commonly gates below-the-fold sections behind
// scroll-reveal JS — an initial ``opacity:0`` / ``visibility:hidden`` state
// that an IntersectionObserver clears on scroll. With scripts blocked those
// sections never reveal, leaving a large blank area in the tile while the
// full-screen dialog (which does run scripts) renders fine. This stylesheet
// neutralises the hidden state and disables entry animations/transitions so
// the static thumbnail mirrors the dialog. ``!important`` beats the
// (near-universally non-important) author reveal-hidden rules regardless of
// source order; ``<base href="about:srcdoc">`` matches the dialog so relative
// URLs resolve to the opaque srcdoc document, not the parent Soku origin.
const THUMBNAIL_FORCE_VISIBLE_SNIPPET = `<base href="about:srcdoc"><style>*,*::before,*::after{opacity:1!important;visibility:visible!important;animation:none!important;transition:none!important}</style>`

/**
 * Insert ``snippet`` as early as possible inside the document head: right
 * after ``<head>`` so an injected ``<base>`` applies to every following
 * resource. Falls back to just-after ``<html>`` or a bare prefix for malformed
 * agent HTML that omits a head.
 */
function injectAtHeadStart(html: string, snippet: string): string {
  const headMatch = html.match(/<head[^>]*>/i)
  if (headMatch?.index != null) {
    const at = headMatch.index + headMatch[0].length
    return html.slice(0, at) + snippet + html.slice(at)
  }
  const htmlMatch = html.match(/<html[^>]*>/i)
  if (htmlMatch?.index != null) {
    const at = htmlMatch.index + htmlMatch[0].length
    return html.slice(0, at) + snippet + html.slice(at)
  }
  return snippet + html
}

/**
 * Wrap agent HTML for the full-screen dialog iframe: inject the link
 * interceptor + ``<base>`` so navigation stays inside the opaque
 * (``allow-scripts``) iframe.
 */
export function wrapHtmlForPreview(
  html: string,
  options: {
    /** Copy for the in-frame note bubble; its presence turns annotating on. */
    elementQuoteLabels?: ArtifactElementQuoteLabels
    reportFontBase?: string
  } = {},
): string {
  const upgradedHtml = upgradeLiteralReportFontStacks(removeLegacyReportFontStyle(html))
  return injectAtHeadStart(
    upgradedHtml,
    LINK_INTERCEPTOR_SNIPPET +
      reportCjkStylesheetLink(upgradedHtml, options.reportFontBase) +
      (options.elementQuoteLabels
        ? buildElementQuoteSnippet(options.elementQuoteLabels)
        : ''),
  )
}

/**
 * Wrap agent HTML for the temporary, hidden print iframe behind the dialog's
 * ``Download as PDF`` action. Injects the same link interceptor + ``<base>`` as
 * {@link wrapHtmlForPreview} (so the printed output matches the on-screen
 * preview) plus a self-triggering ``window.print()`` snippet. The print runs
 * inside the frame because its opaque-origin sandbox blocks the parent from
 * calling ``print()`` cross-origin — see {@link ARTIFACT_PRINT_DONE_MESSAGE}.
 */
export function wrapHtmlForPrint(
  html: string,
  options: { reportFontBase?: string } = {},
): string {
  const upgradedHtml = upgradeLiteralReportFontStacks(removeLegacyReportFontStyle(html))
  return injectAtHeadStart(
    upgradedHtml,
    LINK_INTERCEPTOR_SNIPPET +
      reportCjkStylesheetLink(upgradedHtml, options.reportFontBase) +
      PRINT_TRIGGER_SNIPPET,
  )
}

/**
 * Wrap agent HTML for the non-interactive thumbnail tile. Unlike
 * {@link wrapHtmlForPreview} this injects no script — the thumbnail iframe
 * blocks scripts — only a stylesheet that forces scroll-reveal content visible
 * so the tile does not show a blank area where JS would otherwise have
 * revealed sections.
 */
export function wrapHtmlForThumbnail(html: string): string {
  return injectAtHeadStart(html, THUMBNAIL_FORCE_VISIBLE_SNIPPET)
}

/**
 * Wrap agent HTML for the public share page iframe
 * (``app/share/[token]/page.tsx``). Same interceptor skeleton as
 * {@link wrapHtmlForPreview}, but external http(s) links open a new tab via
 * ``window.open`` instead of a parent relay: the share iframe's sandbox grants
 * ``allow-popups`` + ``allow-popups-to-escape-sandbox``, and without the
 * interceptor a target-less external link navigates the iframe itself — the
 * destination then inherits the sandbox flags, lacks ``allow-same-origin``,
 * and crashes on its first ``document.cookie`` / ``localStorage`` access.
 */
export function wrapHtmlForShare(html: string): string {
  return injectAtHeadStart(html, SHARE_LINK_INTERCEPTOR_SNIPPET)
}

/**
 * Resolve a relayed artifact link into a safe-to-open URL. Returns ``null`` for
 * anything that is not an absolute ``http(s)`` URL so a hostile artifact cannot
 * trigger ``javascript:`` / ``data:`` / ``file:`` navigations on the parent.
 */
export function safeArtifactLinkHref(href: unknown): string | null {
  if (typeof href !== 'string') return null
  let parsed: URL
  try {
    parsed = new URL(href)
  } catch {
    return null
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
  return parsed.href
}
